import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useActivities } from "@/modules/homeDisplay/hooks";
import { refreshConfig } from "@/modules/homeDisplay/services";
import { useUserDataFlattened } from "@/modules/user/stores";
import {
  GroupedRechargeClaimPopup,
  type GroupedRechargeClaimItem,
} from "../components/GroupedRecharge/GroupedRechargeClaimPopup/GroupedRechargeClaimPopup";
import { eventApi } from "@/api/event";
import {
  addGroupedRechargeReadClaimIds,
  clearPendingGroupedClaimConfigResync,
  coerceIdString,
  isPopupItemDismissedInSet,
  loadGroupedRechargeReadClaimIds,
  peekPendingGroupedClaimConfigResync,
  popupDismissKeyFromItem,
} from "../groupedRechargeClaimReadStorage";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";

const GRT_CLAIM_CONFIG = POPUP_CONFIGS.GROUPED_RECHARGE_TASK_CLAIM;

/**
 * 到账金额弹窗：仅本需求；配置 + 队列与 themes 一致，展示逻辑在此处收口。
 * shouldShow 带「条目快照」：避免 PopupProvider 早于子组件执行 render() 且接口瞬时清空 popupItems 时被误判跳过。
 */
export const useGroupedRechargeClaimPopup = () => {
  const { pathname } = useLocation();
  const activities = useActivities();
  const { loadUserInfo, userId, balance } = useUserDataFlattened();
  /** popupItems 内容指纹（不依赖 activities 引用是否更替；充值/入账后常与 balance 一齐变化） */
  const grtPopupFingerprint = useHomeDisplayStore(s => {
    const items = (s.data?.activities as any)?.GROUPED_RECHARGE_TASK
      ?.popupItems as unknown[] | undefined;
    if (!items?.length) return "";
    return items
      .map(
        (it: any) =>
          `${popupDismissKeyFromItem(it) ?? "?"}:${String(it?.rewardAmount ?? it?.reward_amount ?? "")}`
      )
      .join("|");
  });
  const itemsSnapshotRef = useRef<GroupedRechargeClaimItem[]>([]);
  /** 仅用于 useQueuedPopup 内部「队列占位未清」时对 shouldShow 的读值；与返回值 isInQueue 同步 */
  const isInQueueRef = useRef(false);
  const readIdsRef = useRef<Set<string>>(new Set());
  const [readVersion, setReadVersion] = useState(0);
  const [claimCarouselActive, setClaimCarouselActive] = useState(false);

  const popupItems = useMemo((): GroupedRechargeClaimItem[] => {
    const grt = (activities as any)?.GROUPED_RECHARGE_TASK;
    if (!grt) return [];
    const persistedReads = loadGroupedRechargeReadClaimIds(userId);
    const raw: any[] = grt?.popupItems ?? [];
    return raw
      .filter(
        (item: any) =>
          popupDismissKeyFromItem(item) &&
          !isPopupItemDismissedInSet(item, persistedReads) &&
          !readIdsRef.current.has(popupDismissKeyFromItem(item)!)
      )
      .map((item: any) => {
        const ledgerRaw = item?.ledgerId ?? item?.ledger_id;
        const claimRaw = item?.claimId ?? item?.claim_id;
        const ledgerIdStr = coerceIdString(ledgerRaw);
        const claimIdStr = coerceIdString(claimRaw);
        return {
          claimId: claimIdStr,
          ledgerId: ledgerIdStr || undefined,
          grantRoundNo:
            typeof item.grantRoundNo === "number"
              ? item.grantRoundNo
              : item.grantRoundNo != null
                ? Number(item.grantRoundNo)
                : typeof item.grant_round_no === "number"
                  ? item.grant_round_no
                  : item.grant_round_no != null
                    ? Number(item.grant_round_no)
                    : undefined,
          taskGroupId: item.taskGroupId ?? item.task_group_id,
          activityName: item.activityName ?? item.activity_name,
          iconUrl: item.iconUrl ?? item.icon_url,
          bonusIconUrl: item.bonusIconUrl ?? item.bonus_icon_url,
          rewardAmount: Number(
            item.rewardAmount ?? item.reward_amount ?? item.amount ?? 0
          ),
        };
      });
  }, [activities, readVersion, userId, grtPopupFingerprint]);

  /**
   * 队列里的 render() 一旦入队会长期沿用当时闭包：禁止在 shouldShow / render items 里读「快照」的旧
   * popupItems 数组，否则接口先清空 popupItems 后仍可 shouldShow=true（isInQueueRef）但传入空 items，
   * GroupedRechargeClaimPopup 直接 null → 队列误判完成，首页看不到到账提醒。
   */
  const popupItemsRef = useRef(popupItems);
  popupItemsRef.current = popupItems;

  useLayoutEffect(() => {
    if (popupItems.length > 0) {
      itemsSnapshotRef.current = popupItems.map(i => ({ ...i }));
    }
  }, [popupItems]);

  const startClaimCarouselSession = useCallback(() => {
    setClaimCarouselActive(true);
  }, []);

  const shouldShowClaim = useCallback((): boolean => {
    const hasFrozenItems = itemsSnapshotRef.current.length > 0;
    const pi = popupItemsRef.current;
    return (
      pi.length > 0 ||
      claimCarouselActive ||
      hasFrozenItems ||
      isInQueueRef.current
    );
  }, [claimCarouselActive]);

  const popupItemsSig = useMemo(
    () =>
      popupItems.map(i => popupDismissKeyFromItem(i) ?? i.claimId).join(","),
    [popupItems]
  );

  // 充值在非首页完成时，HomePage mount/useActivate 可能在 backend 尚未补齐 popupItems 时拉 config 并覆盖；
  // 会话标记 + 进入首页后短延迟再 refresh，使到账条能入队（与 release-themes 行为对齐）
  useEffect(() => {
    const isHome =
      pathname === ROUTES.HOME ||
      pathname === ROUTES.HOME_ALT ||
      pathname === "/";
    if (!isHome || !peekPendingGroupedClaimConfigResync()) {
      return;
    }
    const tid = window.setTimeout(() => {
      void (async () => {
        try {
          await useHomeDisplayStore.getState().refreshConfig();
        } catch (e) {
          console.error("[GroupedRechargeClaim] 回首页后补拉 config 失败:", e);
        } finally {
          clearPendingGroupedClaimConfigResync();
        }
      })();
    }, 450);
    return () => clearTimeout(tid);
  }, [pathname]);

  useEffect(() => {
    const stored = loadGroupedRechargeReadClaimIds(userId);
    let changed = false;
    stored.forEach(id => {
      if (!readIdsRef.current.has(id)) {
        readIdsRef.current.add(id);
        changed = true;
      }
    });
    if (changed) setReadVersion(v => v + 1);
  }, [userId]);

  const handleDismissItem = useCallback(
    (item: GroupedRechargeClaimItem) => {
      const key = popupDismissKeyFromItem(item);
      if (!key) return;
      readIdsRef.current.add(key);
      setReadVersion(v => v + 1);
      addGroupedRechargeReadClaimIds(userId, [key]);
      const lid = typeof item.ledgerId === "string" ? item.ledgerId.trim() : "";
      const cid = typeof item.claimId === "string" ? item.claimId.trim() : "";
      if (lid) {
        eventApi
          .markGroupedRechargeTaskPopupRead({ ledgerId: lid })
          .catch(() => undefined);
      } else if (cid) {
        eventApi
          .markGroupedRechargeTaskPopupRead({ claimId: cid })
          .catch(() => undefined);
      }
    },
    [userId]
  );

  const handleRefreshAfterClose = useCallback(() => {
    void (async () => {
      try {
        await loadUserInfo();
        await refreshConfig();
      } catch (e) {
        console.error("[GroupedRechargeClaim] 关闭后刷新失败:", e);
      }
    })();
  }, [loadUserInfo]);

  const { isInQueue } = useQueuedPopup({
    popupId: GRT_CLAIM_CONFIG.id,
    priority: GRT_CLAIM_CONFIG.priority,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    shouldShow: shouldShowClaim,
    onComplete: () => {
      itemsSnapshotRef.current = [];
    },
    renderContent: onQueueComplete => (
      <GroupedRechargeClaimPopup
        items={
          popupItemsRef.current.length > 0
            ? popupItemsRef.current
            : [...itemsSnapshotRef.current]
        }
        onCarouselSessionStart={startClaimCarouselSession}
        onDismiss={handleDismissItem}
        onAllClosed={() => {
          setClaimCarouselActive(false);
          handleRefreshAfterClose();
          onQueueComplete();
        }}
      />
    ),
    showOnce: false,
    allowManualTrigger: false,
    autoTrigger: true,
    /** 有 popupItems 时立即入队；同 popupId 全局只保留一条，回首页再走 allowedPaths + render */
    allowEnqueueWhenNotHome: true,
    dependencies: [
      pathname,
      popupItemsSig,
      readVersion,
      claimCarouselActive,
      grtPopupFingerprint,
      balance ?? 0,
    ],
    userBinding: false,
  });

  isInQueueRef.current = isInQueue;

  return {
    hasClaimPopup: popupItems.length > 0 || claimCarouselActive,
  };
};
